.branch {
  display: flex;
  flex-direction: column;
  gap: var(--space-m);

  margin: 0;
  padding: 0;

  list-style: none;
}

.node {
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
}

.topic {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);

  scroll-margin-block: var(--space-xl);
}

.topicHead {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-s);
}

.topicTitle {
  font-size: var(--size-400);
  font-weight: 500;
}

.weight {
  font-size: var(--size-100);
  font-variant-numeric: tabular-nums;

  color: var(--text-faint);
}

.count {
    display: flex;
    padding-inline: var(--space-s);
    background-color: #ffffff14;
    border-radius: var(--radius-pill);
}

.topicActions {
  display: inline-flex;
  gap: var(--space-2xs);
}

.topicDescription {
  font-size: var(--size-200);
  padding-left: 32px;
  color: var(--text-muted);
}

.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
}

.sectionTitle {
  font-size: var(--size-100);
  font-weight: var(--weight-strong);
  letter-spacing: 0.03em;
  text-transform: uppercase;

  color: var(--text-faint);
}

.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);

  margin: 0;
  padding: 0;

  list-style: none;
}

.item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-xs);

  padding: 6px 10px;

  font-size: var(--size-200);

  background-color: var(--surface-sunken);
  border-radius: var(--radius-xs);

  scroll-margin-block: var(--space-xl);
}

/*
 * The row the URL points at, marked so a reader scrolled back to it after a
 * save can tell which one that was. Outlined rather than filled, because the
 * fill already says what kind of row it is.
 */
.topic:target,
.item:target {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.item:first-of-type {
    border-top-left-radius: var(--radius-m);
    border-top-right-radius: var(--radius-m);
}

.item:last-of-type {
    border-bottom-left-radius: var(--radius-m);
    border-bottom-right-radius: var(--radius-m);
}

/*
 * The hue names the kind of node, nothing more. Answer coverage is a separate
 * axis and rides on a labelled badge instead, so it survives being printed,
 * being read by somebody colour-blind, and a question being both at once.
 */
.itemQuestion {
  color: var(--node-question-text);
  background-color: var(--node-question-surface);
}

.itemCapability {
  color: var(--node-capability-text);
  background-color: var(--node-capability-surface);
}

.itemTitle {
  font-weight: var(--weight-medium);
}

/*
 * How a report treats this question's answers. Bordered rather than filled, so
 * it reads as a mark on the row instead of competing with the row's own colour.
 */
.coverage {
  padding: 0 var(--space-xs);

  font-size: var(--size-100);
  font-weight: 600;

  border: 1px solid currentColor;
  border-radius: var(--radius-pill);
}

/* Answered and inside the view: the ordinary case, so it stays quiet. */
.coverageCounted {
  color: var(--node-question-text);

  opacity: 0.75;
}

/*
 * Answered, but no report on this view reaches it. This is the state that has
 * cost real debugging time, so it is the loudest thing on the row.
 */
.coverageOutside {
  color: var(--band-missing);

  border-width: 2px;
}

/* Never answered, which is a gap to fill rather than a fault to fix. */
.coverageUnanswered {
  color: var(--text-faint);

  border-style: dashed;
}

.itemMeta {
  font-size: var(--size-100);
  font-weight: var(--weight-medium);

  color: var(--text-faint);
}

.informational {
  padding: 0 var(--space-xs);

  font-size: var(--size-100);
  font-weight: 600;

  color: #fff;
  background-color: rgb(255 255 255 / 8%);
  border-radius: var(--radius-s);
}

/*
 * Row actions stay out of the way until the row is pointed at. They keep their
 * space either way, so revealing one never shifts the list, and focus reveals
 * them too, or the keyboard would tab to something invisible.
 */
.itemActions {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: var(--space-2xs);

  margin-inline-start: auto;

  opacity: 0;

  transition: opacity var(--duration-fast) var(--ease);
}

.item:hover .itemActions,
.itemActions:focus-within {
  opacity: 1;
}

.topicActions {
  align-self: center;

  opacity: 0;

  transition: opacity var(--duration-fast) var(--ease);
}

.topic:hover .topicActions,
.topicActions:focus-within {
  opacity: 1;
}
